<template>
  <div class="selection-body">
    <button class="view-cart-button" @click="setCartState" v-if="!isMagento">
      {{ $t('cart.button_text') }}
    </button>

    <transition name="fade" mode="out-in">
      <div class="cart-dropdown"
           v-if="isCartOpen"
           :class="{'active': isCartOpen}">
        <order-cart @close="setCartState"></order-cart>
      </div>
    </transition>

    <div class="container-x">
      <div class="row no-gutters" v-if="!isLoading">
        <template v-if="canShowModule(['workplace', 'and_pump', 'industrial'])">
          <div class="col-12">
            <div class="alert alert-warning mt-4" v-if="!elasticCoupling">
              {{ $t('powerpack.configurator.elasticCouplingError') }}
            </div>
            <div class="alert alert-warning mt-4" v-if="!lanternPiece">
              {{ $t('powerpack.configurator.lanternPieceError') }}
            </div>
          </div>
        </template>

        <template v-if="device === 'mobile'">
          <sidebar :title="$t('powerpack.configurator.menu')">
            <overview-menu :motor="motor" :tank="tank" :pump="pump"></overview-menu>
            <Menu class="d-md-block"/>
            <FileMenu class="d-md-block"/>
            <ValvesMenu
              v-if="canShowModule(['mini_powerpack', 'workplace', 'and_pump', 'open_frame', 'closed_frame', 'industrial'])"/>
          </sidebar>

          <div class="col-11 flex-grow-1 col-md offset-1" style="margin-right: auto">
            <powerpack-image-container/>
            <price @openModal="showPopup = true"></price>
          </div>
        </template>

        <template v-else>
          <div class="col-md mr-md-2 flex-grow-0">
            <router-link :to="{name: 'powerpack-motor-configurator'}" tag="a" class="d-none d-md-block">
              <img src="../../assets/link-left-arrow.png"> {{ $t('powerpack.configurator.back') }}
            </router-link>

            <overview-menu :motor="motor" :tank="tank" :pump="pump"></overview-menu>
            <Menu class="d-none d-md-block"/>
            <FileMenu class="d-none d-md-block"/>
          </div>
          <div class="col-12 flex-grow-1 col-md" style="margin-right: auto">
            <ValvesMenu @openModal="showPopup = true"
                        v-if="canShowModule(['mini_powerpack', 'workplace', 'and_pump', 'open_frame', 'closed_frame', 'industrial'])"/>
            <powerpack-image-container/>
          </div>
        </template>
      </div>
    </div>
    <popup-shopping-cart :generator="$t('cart.powerpack')" @close="showPopup = false"
                         v-if="showPopup"></popup-shopping-cart>
  </div>
</template>

<script>
import Menu from './menu/Menu';
import PowerpackImageContainer from './PowerpackImageContainer';
import {mapActions, mapGetters, mapState} from 'vuex';
import ValvesMenu from './valves/Valves';
import OverviewMenu from './OverviewMenu';
import OrderMenu from './order/Order';
import showModuleMixin from '../../mixins/showModuleMixin';
import Price from './price/Price';
import Sidebar from '../sidebar/Sidebar';
import PopupShoppingCart from '@/components/popup/PopupShoppingCart';
import FileMenu from '@/components/step2/FileMenu.vue';
import OrderCart from '@/components/step2/order/OrderCart.vue';
import { getBuildUidFromUrl } from '../../helpers/route';
import googleAnalyticsMixin from '@/mixins/googleAnalyticsMixin';

export default {
  name: 'selectionBody',

  components: {
    OrderCart,
    FileMenu,
    OrderMenu,
    Menu,
    PowerpackImageContainer,
    ValvesMenu,
    OverviewMenu,
    Price,
    Sidebar,
    PopupShoppingCart,
  },

  data() {
    return {
      mobileConfig: false,
      showPopup: false,
    };
  },

  mixins: [showModuleMixin, googleAnalyticsMixin],

  computed: {
    ...mapGetters({
      motorOption: 'preference/getMotorOption',
      device: 'getDevice',
      elasticCoupling: 'build/getElasticCoupling',
      lanternPiece: 'build/getLanternPiece',
      isMagento: 'preference/isMagento',
    }),
    ...mapState({
      isLoading: state => state.preference.isLoading,
      motor: state => state.motors.byId[state.build.motor],
      tank: state => state.tanks.byId[state.build.tank],
      pump: state => state.pumps.byId[state.build.pump],
      isCartOpen: state => state.cart.isCartOpen,
      cartItems: state => state.cart.cartItems,
    }),
  },
  methods: {
    setCartState() {
      if (this.isCartOpen) {
        this.$store.commit('cart/setCartState', false);
        return;
      }

      this.pushDataLayer({
        interaction_type: this.gaData.interactionTypes.overviewSystem,
        build_id: this.cartItems.map(item => item.uid).join(','),
        value: this.cartItems.map(item => (item.price / 100).toFixed(2)).join(','),
        step: this.gaData.undefined,
        field: this.gaData.undefined
      })

      this.$store.commit('cart/setCartState', true);
    }
  },
  watch: {
    isLoading: {
      async handler(loading) {
        if (loading) {
          return;
        }

        const buildUuid = getBuildUidFromUrl();
        if (buildUuid) {
          const build = await this.$store.dispatch('build/fetchBuild', buildUuid);

          if (build) {
            await this.$store.dispatch('build/loadBuild', build);
          }
        }

        if (!this.$store.state.build.motor) {
          try {
            await this.$store.dispatch('build/loadBuild');
          } catch (e) {
            console.log('Error loading powerpack', e);
            console.log(e);
          }
        }
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss">
.selection-body {
  position: relative;

  .view-cart-button {
    position: sticky;
    right: 0;
    top: 0;
    left: 100%;
    z-index: 8;
    background: #FFFFFF;
    color: #71BC03;
    font-weight: 600;
    border: 2px solid #71BC03;
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;

    &:hover {
      background: rgba(#71BC03, 10%);
    }

    &:focus {
      outline: none;
    }
  }

  .cart-dropdown {
    z-index: 8;
    background: white;
    min-width: 420px;
    width: auto;
    position: absolute;
    top: 40px;
    right: 0;
    border-radius: 3px;
    padding: 20px;
    overflow: hidden;
    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, .26) !important;

    @media only screen and (max-width: 512px) {
      right: 0;
      width: 100%;
      min-width: 100%;
    }
  }

  .container {
    @media only screen and (max-width: 768px) {
      padding: 0;
    }
  }

  a {
    display: inline-block;
    text-decoration: underline;
    color: #3C3C3B;
    font-family: neue-haas-grotesk-text, sans-serif;
    opacity: 0.5;
    font-size: 12px;
    padding: 13px 0 9px 0;
  }

  .menu-container {
    width: 270px;

    .dropdown-block {
      display: flex;
      flex-direction: column;
      padding-bottom: 19px;
      font-size: 14px;

      &:last-child {
        border: 0;
        padding: 0;
      }

      #dropdownMenuButton > button {
        background-color: white;
        color: #3DB0C7;
        border-radius: 50px;
        border: 2px solid #3DB0C7;
      }
    }
  }

  .first-menu {
    margin-top: 20px;
    background-color: #FAFAFA;
    /*box-shadow: 1px 1px 2px 0 rgba(0, 0, 0, 0.16);*/
    border-right: 3px solid #F0F0F0;
    border-bottom: 3px solid #F0F0F0;
    padding: 17px 14px 20px 17px;
  }

  .second-menu {
    background-color: white;
    margin-top: 30px;
    /*box-shadow: 0 4px 10px rgba(220, 220, 220, 100%);*/

    input {
      width: 100%;
      height: 40px;
      padding: 0 10px;
      border-radius: 50px;
      border: 2px solid #3DB0C7;

      &:focus, &:active {
        outline: none;
        box-shadow: none;
      }
    }
  }

  .second-menu {
    background-color: white;
    margin-top: 30px;
    border-radius: 5px;
    box-shadow: 0 4px 10px rgba(220, 220, 220, 100%);


    span.unit {
      right: 40px;
    }
  }

  .third-menu {
    margin-top: 30px;
    background-color: white;
    border-radius: 50px;
    box-shadow: 0 4px 10px rgba(220, 220, 220, 100%);

    .btn {
      width: 100%;
      height: 40px;
      padding: 0 10px;
      border-radius: 50px;
      border: 2px solid #3DB0C7;

      &:focus, &:active {
        outline: none;
        box-shadow: none;
      }
    }

    .valve-selection {
      width: 100%;
      border-radius: 50px;
      border: 2px solid #3DB0C7;
      display: flex;
      flex-direction: row;
      padding-top: 5px;

      label {
        z-index: 2;
      }

      input[name='valve'] {
        appearance: none;
        outline: none;
        position: relative;
        left: -120px;

        &:after {
          text-align: center;
          content: '';
          padding: 0 10px;
          position: relative;
          border-radius: 15px;
        }

        &:checked {
          &:after {
            background-color: #3DB0C7;
            box-shadow: 2px 2px 14px rgba(0, 0, 0, .4);
            color: rgba(255, 255, 255, .6);
          }
        }
      }
    }

    label.checkbox {
      border-radius: 50px;
      border: 2px solid #3DB0C7;
      padding: 5px;
      margin-top: 20px;
    }

    input[type=checkbox] {
      appearance: none;
      outline: none;
      position: relative;
      left: 0;

      &:after {
        text-align: center;
        content: '';
        padding: 0 10px;
        position: relative;
        border-radius: 15px;
      }

      &:checked {
        &:after {
          background-color: #3DB0C7;
          box-shadow: 2px 2px 14px rgba(0, 0, 0, .4);
          color: rgba(255, 255, 255, .6);
        }
      }
    }

    span {
      float: right;
    }
  }
}

.fade-move-left-enter-active, .fade-move-left-leave-active {
  transition: all .25s ease;
}

.fade-move-left-enter, .fade-move-left-leave-to /* .fade-leave-active below version 2.1.8 */
{
  transform: translateX(-100%);
}

.sidebar {
  display: flex;
  justify-content: space-between;
  background: #3DB0C7;
  color: #fff;
  position: fixed;
  left: 0;
  top: 100px;
  padding: 0 15px;
  font-family: neue-haas-grotesk-text, sans-serif;
  font-size: 13px;
  transform-origin: top right;
  transform: translateX(-100%) rotate(270deg);
  height: 30px;
  line-height: 30px;
  width: 200px;
  transition: all .3s ease;
  z-index: 2;

  &.open {
    left: 100%;
    transform: translateX(-100%) rotate(270deg) translateY(-100%);

    img {
      transform: rotate(180deg);
    }
  }
}

.mobile-configuration {
  position: fixed;
  top: 80px;
  right: 30px;
  left: 0;
  bottom: 0;
  z-index: 99;
  overflow: scroll;
  box-shadow: 0 5px 15px rgba(0, 0, 0, .1);
  background-color: #f9f9f9;

  .image-container {
    position: relative;
    height: auto;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 275px;
      min-width: 80%;
      height: auto;
      max-height: 80vh;
      object-fit: contain;
      object-position: center;
    }
  }
}

.magento {
  .selection-body {
    padding-top: 0;
  }
}
</style>
